import { InspectionFormData, InspectionModalData } from '@/types/model/inspection-modal';
import { 
  InspectionFilters,
  InspectionGroup,
  InspectionGridRow,
  Inspection
} from '../types/model/inspection';
import { apiService } from './ApiService';
import { dataManager } from './data/DataManager';
import { FileResponse } from '@/types/response/InspectionResultResponses';
import { InspectionLineItemResponse, InspectionResultGroupResponse } from '@/types/response/InspectionResultResponses';


interface GroupIdentifiers {
  id: number;
  title: string;
  groupIdentifier: number;
}

interface LineItemIdentifiers {
  id: number;
  lineItem: string;
  title: string;
}

interface InspectionIdentifiers {
  id: number;
  siteId: number;
  areaOfExpertiseId: number;
  inspectedOn: string | null;
  scheduledDate?: string;
  inspectorId: number;
}

interface LookupCache {
  loading: boolean;
  loaded: boolean;
  groups: Map<number, GroupIdentifiers> | null;
  lineItems: Map<number, LineItemIdentifiers> | null;
  inspectionGroups: Map<number, Map<number, Map<number, InspectionIdentifiers>>> | null;
}

class InspectionService {
  
  private cache: LookupCache = {
    loading: false,
    loaded: false,
    groups: null,
    lineItems: null,
    inspectionGroups: null
  };

  public async refreshLookupCache() {
    this.cache.loading = true;
    Promise.all([
      apiService.getContractInspectionResults()
    ]).then(([inspectionGroups]) => {
      this.reloadInspectionResultsData(Promise.resolve(inspectionGroups));
     }).finally(() => {
      this.cache.loading = false;
      this.cache.loaded = true;
    });
  }

  private async reloadInspectionResultsData(inspectionGroups: Promise<InspectionResultGroupResponse[]>) {
    const resolvedInspectionGroups = await inspectionGroups;
    if(resolvedInspectionGroups) {
      const groupsMap = new Map<number, GroupIdentifiers>();
      const lineItemsMap = new Map<number, LineItemIdentifiers>();
      const inspectionGroupsMap = new Map<number, Map<number, Map<number, InspectionIdentifiers>>>();
      resolvedInspectionGroups.map( group => {
        groupsMap.set(group.Id, {
          id: group.Id,
          title: group.Title,
          groupIdentifier: group.Identifier
        });
        
        group.LineItems?.forEach(lineItem => {
          lineItemsMap.set(lineItem.Id, {
            id: lineItem.Id,
            lineItem: lineItem.LineItem,
            title: lineItem.Title
          });

        lineItem.InspectionResults?.forEach(inspectionResult => {
          if (!inspectionGroupsMap.has(group.Id)) {
            inspectionGroupsMap.set(group.Id, new Map<number, Map<number, InspectionIdentifiers>>());
          }
          const lineItemsMapForGroup = inspectionGroupsMap.get(group.Id)!;
          if (!lineItemsMapForGroup.has(lineItem.Id)) {
            lineItemsMapForGroup.set(lineItem.Id, new Map<number, InspectionIdentifiers>());
          }
          const inspectionsMapForLineItem = lineItemsMapForGroup.get(lineItem.Id)!;
          inspectionsMapForLineItem.set(inspectionResult.Id, {
            id: inspectionResult.Id,
            siteId: inspectionResult.SiteId,
            areaOfExpertiseId: lineItem.ExpertiseAreaId,
            inspectedOn: inspectionResult.InspectedOn,
            scheduledDate: inspectionResult.ScheduledDate,
            inspectorId: inspectionResult.UserId
          });
        });
        });
      });

      this.cache.groups = groupsMap;
      this.cache.lineItems = lineItemsMap;
      this.cache.inspectionGroups = inspectionGroupsMap;
    }
  }

  public getInspectionIdentifiersMap(groupId: number, lineItemId: number): Map<number, InspectionIdentifiers> | null {
    return this.cache.inspectionGroups?.get(groupId)?.get(lineItemId) ?? null;
  }
  
  public async getGroupedInspectionData(groupIds: number[] = [], siteIds: number[] = [], areaIds: number[] = [], from?: string, to?: string): Promise<InspectionGroup[]> {
    return await dataManager.getGroupedInspectionData(groupIds, siteIds, areaIds, from, to);
  }

  public async getInspections(ids: number[]): Promise<Inspection[]> {
    return await dataManager.getInspections(ids);
  }

  public async getContractInspection(inspectionResultId: number): Promise<InspectionLineItemResponse> {
    return await dataManager.getContractInspection(inspectionResultId);
  }

  public async getContractExistingResources(): Promise<FileResponse[]> {
    return await dataManager.getContractExistingResources();
  }
  
  public async filterInspectionGroups(groups: InspectionGroup[], filters: InspectionFilters): Promise<InspectionGroup[]> {
    if (filters.showAll) {
      return groups;
    }

    return groups.map(group => ({
        ...group,
        lineItems: group.lineItems?.map(lineItem => ({
            ...lineItem,
            subItems: lineItem.subItems?.filter(inspectionSubItem => {
              // const inspectionSubItem = lineItem.subItems?.find(sub => sub.id === inspection.inspectionResultId);
              if (!inspectionSubItem) return false; // Shouldn't happen, but safety check
              return filters.selectedStatuses.includes(inspectionSubItem.status);
            }) ?? null
        })).filter(lineItem => (lineItem.subItems?.length ?? 0) > 0) ?? null
    })).filter(group => (group.lineItems?.length ?? 0) > 0);
  }

  async saveInspection(
    inspectionData: InspectionModalData, 
    inspectionFormData: InspectionFormData, 
    originalFormData: InspectionFormData): Promise<void> {
    await dataManager.saveInspection(inspectionData, inspectionFormData, originalFormData);
  }

  public async getInspectionForGroups(groups: InspectionGroup[]): Promise<InspectionGroup[]> {

      const groupsWithInspections: InspectionGroup[] = groups.map(group => ({
          groupIdentifier: group.groupIdentifier,
          id: group.id,
          title: group.title,
          lineItems: group.lineItems?.map(li => ({ ...li, inspections: null })) ?? null,
      }));

      // Collect all line items across groups
      const allLineItems: { groupIdx: number; lineItemIdx: number }[] = [];
      groupsWithInspections.forEach((group, gIdx) => {
          (group.lineItems ?? []).forEach((_, liIdx) => {
              allLineItems.push({ groupIdx: gIdx, lineItemIdx: liIdx });
          });
      });

      // Use promise pool to fetch inspections for each line item with a concurrency limit
      const concurrencyLimit = 3;
      const delayBetweenBatches = 500; // in milliseconds
      for (let i = 0; i < allLineItems.length; i += concurrencyLimit) {
          const batch = allLineItems.slice(i, i + concurrencyLimit);
          await Promise.all(batch.map(async ({ groupIdx, lineItemIdx }) => {
              const lineItem = groupsWithInspections[groupIdx].lineItems![lineItemIdx];
              if ((lineItem.subItems?.length ?? 0) > 0) {
                  lineItem.inspections = await this.getInspections(lineItem.subItems?.map(sub => sub.id) ?? []);
              }
          }));
          if (i + concurrencyLimit < allLineItems.length) {
              await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
          }
      }
      return groupsWithInspections;
  }

  public async persistEssentialData(groupedData: InspectionGroup[]): Promise<boolean> {
    return await dataManager.cacheInspections(groupedData);
  }

  /** Flattens groups into one row per inspection, for the grid view. Fetches full inspection details in a single batch call. */
  public async getFlattenedInspectionItems(groups: InspectionGroup[]): Promise<InspectionGridRow[]> {
    const allIds: number[] = [];
    for (const group of groups) {
      for (const lineItem of group.lineItems ?? []) {
        for (const subItem of lineItem.subItems ?? []) {
          allIds.push(subItem.id);
        }
      }
    }

    if (allIds.length === 0) return [];

    const inspections = await this.getInspections(allIds);
    const inspectionsById = new Map(inspections.map(inspection => [inspection.inspectionResultId, inspection]));

    const rows: InspectionGridRow[] = [];
    for (const group of groups) {
      for (const lineItem of group.lineItems ?? []) {
        for (const subItem of lineItem.subItems ?? []) {
          const inspection = inspectionsById.get(subItem.id);
          if (!inspection) continue;

          rows.push({
            ...inspection,
            status: subItem.status,
            timeSensitivity: subItem.timeSensitivity,
            scheduledDate: subItem.scheduledDate,
            groupId: group.id,
            groupIdentifier: group.groupIdentifier,
            groupTitle: group.title,
          });
        }
      }
    }
    return rows;
  }
}

export const inspectionService = new InspectionService();
export default inspectionService;